import { Paper, ScrollArea, Button, ThemeIcon, Center, Loader, useMantineTheme, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconClipboardCopy, IconTrash } from "@tabler/icons-react";
import cx from "clsx";

import { ListWorkOrderTemplateDto } from "../../models/workordertemplate.models";
import { useFilteredWorkOrderTemplates } from "../../functions/workordertemplate-filter.hooks";
import { useSearch } from "../../functions/SearchBox.hook";
import { NoSearchResults } from "../Shared/NoSearchResults";
import classes from './WorkOrderTemplateList.module.css';

interface WorkOrderTemplateListProps {
	templates?: ListWorkOrderTemplateDto[] | undefined;
	isLoading?: boolean;
	selectedTemplate?: ListWorkOrderTemplateDto | null;
	setSelectedTemplate?: (template: ListWorkOrderTemplateDto | null) => void;
}

export const WorkOrderTemplateList = ({ templates, isLoading, selectedTemplate, setSelectedTemplate }: WorkOrderTemplateListProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { query, setQuery } = useSearch();
	const { filteredTemplates, showNoResults } = useFilteredWorkOrderTemplates(templates || []);

	if (isLoading) return <Paper withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)', minHeight: isMobile ? 'calc(100vh - 150px)' : 'calc(100vh - 160px)' }} >
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>

	const handleTemplateSelect = (e: React.MouseEvent<HTMLAnchorElement>, template: ListWorkOrderTemplateDto): void => {
		e.preventDefault();
		setSelectedTemplate?.(template);
	}

	return <>
		<Paper withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)', minHeight: isMobile ? 'calc(100vh - 150px)' : 'calc(100vh - 160px)' }} >
				{filteredTemplates?.map(template => (
					<Button key={template.WorkOrderTemplateId}
						component="a"
						href={`/workordertemplates/${template.WorkOrderTemplateId}`}
						onClick={(e) => handleTemplateSelect(e, template)}
						className={cx(classes['template-list-item'], { [classes['selected-item']]: selectedTemplate?.WorkOrderTemplateId === template.WorkOrderTemplateId })}
						leftSection={
							<ThemeIcon radius="xl"
								color={template.Deleted ? "red" : "indigo"}
								size="xl"
								style={{ border: `1px solid var(--mantine-color-default-border)` }}
								variant="light"
							>
								{!template.Deleted ? <IconClipboardCopy size={24} /> : <IconTrash size={24} />}
							</ThemeIcon>
						}
					>
						<Text c={template.Deleted ? "dimmed" : ""}>{template.Title}</Text>
					</Button>
				))}
				{showNoResults && <NoSearchResults searchType="templates" query={query} setQuery={setQuery} />}
				{templates?.length === 0 && <>
					<Stack align="center" justify="center" mt="xl" px="sm">
						<ThemeIcon size={100} radius={100} variant="light" color="gray">
							<IconClipboardCopy size={60} />
						</ThemeIcon>
						<Text style={{ textAlign: 'center' }} lineClamp={1} c="dimmed">No templates have been created yet.</Text>
					</Stack>
				</>}
			</ScrollArea>
		</Paper>
	</>
}
